// FLAGS: none
// The Spaces app's view of the Convene API: request and response shapes, and the content-language set.
/**
 * Domain types + API DTOs for the OpenUp Spaces admin UI (OI-24710).
 *
 * OI-24710 is UI-first: these shapes ARE the contract the backend stories
 * implement against — OI-24709 (persistence: SpaceEvent -> Space), OI-24711
 * (Zoom write + hostable-expert intersect), OI-24713 (email invites),
 * OI-24714 (shared link). All endpoints live under /api/v1/spaces/*, are
 * cookie-authed, and gated by CustomerSuccessPolicy.
 *
 * A "Private Space" is modelled per db_design.md Option C: a SpaceEvent
 * (parent: title/description/employer/audience) with one or more sessions
 * (each a Space: date, end, capacity, Zoom webinar, experts).
 */

/** Active employer for the company dropdown. GET /spaces/employers. */
export interface Employer {
  id: string;
  name: string;
}

/**
 * An expert eligible to host on Zoom — the MOU expert ∩ Zoom licensed-user
 * intersection (resolved live by email, OI-24711). The picker only offers
 * these for the Host role; create still re-checks the licence (fail-fast).
 */
export interface HostableExpert {
  userId: string;
  fullName: string;
  email: string;
  /** Whether the expert currently holds a Zoom licence (host-capable). */
  licensed: boolean;
}

/**
 * Event sub-type. A group session is a Zoom meeting (everyone collaborates);
 * a masterclass is a Zoom webinar (panelists present, attendees watch). Set at
 * the event grain — all sessions of an event share it.
 */
export type SpaceEventType = "groupsession" | "masterclass";

/**
 * Role an expert plays in a session. Host → Zoom alternative host (both types).
 * The non-host role is type-specific: "panelist" for a masterclass (webinar),
 * "cohost" for a group session (meeting) — both meeting elevated roles are
 * licensed Zoom users.
 */
export type ExpertRole = "host" | "panelist" | "cohost";

/** One session's expert assignment, captured in the create form. */
export interface SessionExpertInput {
  userId: string;
  role: ExpertRole;
}

/** A single session in the create payload (-> one Space row). */
export interface SessionInput {
  /**
   * Optional session name -> `Space.Title`. `Space.Title` is NOT NULL, so the
   * backend falls back to the event title when this is null (OI-24709/24711).
   */
  name: string | null;
  /** Local date, "YYYY-MM-DD". */
  date: string;
  /** Local start time, "HH:mm". */
  startTime: string;
  /**
   * Session length in minutes. The picker offers 30/45/60/90, but the End-time toggle derives any
   * value from the chosen end, so this is not a closed set — the API accepts 1..1440.
   */
  durationMinutes: number;
  /** Optional practical notes for attendees, included in confirmation/reminder emails. */
  sessionInstructions: string | null;
  /** Optional IANA timezone; the backend defaults to "Europe/Amsterdam". */
  timeZone: string | null;
  /** Optional seat cap; the backend defaults to 100. */
  capacity: number | null;
  experts: SessionExpertInput[];
  /**
   * This session's copy in languages other than the event's own. Nested here
   * rather than keyed by session id (the shape the edit PATCH uses) because the
   * session has no id until this request creates it.
   */
  translations: SessionTranslation[];
}

/** POST /spaces/events request body. Audience is implicitly Private here. */
export interface CreateSpaceEventRequest {
  title: string;
  description: string | null;
  eventType: SpaceEventType;
  /** Every company the event is open to; at least one is required. */
  employerIds: string[];
  /**
   * Audience name for the public landing page. Required once more than one
   * company is selected: no single company name describes the audience, and the
   * parent company usually has no record of its own.
   */
  employerDisplayName: string | null;
  /**
   * Third-party survey attendees are sent to when a session ends (Zoom
   * `settings.survey_url`). Masterclasses only — the backend rejects it for
   * group sessions (meetings have no post-session survey).
   */
  surveyUrl: string | null;
  /**
   * The language `title`/`description` and the sessions' own copy are written
   * in. Required, unlike on the edit PATCH: an event created without one would
   * have its emails sent in the fallback language until someone reopened it in
   * edit mode to declare one.
   */
  languageCode: ContentLanguage;
  /** Event copy in languages other than `languageCode`; optional. */
  translations: EventTranslation[];
  sessions: SessionInput[];
}

/** Lifecycle status of a created session (mirrors Space.Status + Zoom state). */
export type SessionStatus = "scheduled" | "cancelled";

export interface SpaceSession {
  id: string;
  /** `Space.Title` — the session name (defaults to the event title). */
  title: string;
  /**
   * True when the title is a copy of the event title (scheduler left the name
   * blank) — such titles follow event renames. The edit form shows a blank
   * name for these so saving doesn't turn the copy into an explicit name.
   */
  titleIsDefaulted: boolean;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  capacity: number | null;
  /** Practical notes for attendees (confirmation/reminder emails); null when not set. */
  sessionInstructions: string | null;
  /** Zoom webinar id once created (null while pending / on failure). */
  externalWebinarId: string | null;
  /** Session copy in every language except the event's source language. */
  translations: SessionTranslation[];
  status: SessionStatus;
  experts: Array<{
    userId: string;
    fullName: string;
    role: ExpertRole;
  }>;
}

/**
 * A content language an event can be authored or translated into. Mirrors
 * ConveneLanguages on the backend and the MyOpenUp locale domains.
 */
export type ContentLanguage = "en-GB" | "nl-NL" | "de-DE" | "fr-FR" | "es-ES";

/** Offered in the order the UI lists them; the backend accepts exactly these. */
export const CONTENT_LANGUAGES: ContentLanguage[] = [
  "en-GB",
  "nl-NL",
  "de-DE",
  "fr-FR",
  "es-ES",
];

/** Human label for a language, shown in its own language so a reader finds it. */
export const CONTENT_LANGUAGE_LABELS: Record<ContentLanguage, string> = {
  "en-GB": "English",
  "nl-NL": "Nederlands",
  "de-DE": "Deutsch",
  "fr-FR": "Français",
  "es-ES": "Español",
};

/**
 * Event copy in one language other than the event's source language. Both
 * fields are independently optional: a blank one falls back to the event's own
 * copy rather than blanking the page.
 */
export interface EventTranslation {
  languageCode: ContentLanguage;
  title: string | null;
  description: string | null;
}

/** Session copy in one language other than the event's source language. */
export interface SessionTranslation {
  languageCode: ContentLanguage;
  title: string | null;
  sessionInstructions: string | null;
}

export interface SpaceEvent {
  id: string;
  title: string;
  description: string | null;
  eventType: SpaceEventType;
  /** Every company this event is open to, in name order. */
  employers: Employer[];
  /**
   * What the customer is called wherever an attendee or expert sees it: the
   * display name when the scheduler set one, otherwise the single company's own
   * name. Resolved by the backend so every surface agrees.
   */
  employerName: string;
  /**
   * Audience name shown instead of the company's own; null when the single
   * company's name is used. Large customers exist here as several company rows,
   * one per legal entity, and the parent company often has no record at all.
   */
  employerDisplayName: string | null;
  /** Always "private" for events created in this app. */
  audience: "private";
  /** Display name of the CS scheduler who created it (SpaceEvent.SchedulerUserId). */
  scheduledBy: string;
  /** Post-session third-party survey URL; null when none is set (masterclasses only). */
  surveyUrl: string | null;
  /**
   * Which language `title`/`description` are written in. Null for events created
   * before multilingual content — the scheduler declares it on the next edit,
   * and no translation can be added until they do.
   */
  languageCode: ContentLanguage | null;
  /** Every language this event can be read in: its own plus each translated one. */
  availableLanguageCodes: ContentLanguage[];
  /** The public event page, one copyable link per language. */
  publicPageUrls: LocalizedLink[];
  createdAt: string;
  sessions: SpaceSession[];
  /** Event copy in every language except `languageCode`. */
  translations: EventTranslation[];
}

/**
 * Summary row for the events list. The list is shared across CS schedulers
 * (all see all Private Spaces), so each row carries who scheduled it.
 */
export interface SpaceEventSummary {
  id: string;
  title: string;
  eventType: SpaceEventType;
  employerName: string;
  scheduledBy: string;
  sessionCount: number;
  /** Earliest upcoming (or earliest) session start, ISO; null if none. */
  nextSessionAt: string | null;
  /** End of the event's latest session, ISO; null when it has no sessions. */
  lastSessionEndsAt: string | null;
}

/**
 * GET /convene/events — the shared events list split for the main page:
 * upcoming (any session still to end, or no sessions yet; next session
 * soonest first) and past (every session ended, most recently ended first).
 * The backend owns the split so no client clock comparison is needed.
 */
export interface SpaceEventsOverview {
  upcoming: SpaceEventSummary[];
  past: SpaceEventSummary[];
  /** Every session cancelled — listed apart, most recent first. */
  cancelled: SpaceEventSummary[];
}

/**
 * Structured create error so the form can map a failure back to the
 * offending session/host (fail-fast invariants from db_design.md §4.6/§4.7).
 */
export type CreateSpaceEventErrorCode =
  | "NO_HOST"
  | "UNLICENSED_HOST"
  | "VALIDATION"
  /**
   * The request never reached a verdict: a 5xx, a timeout, a dropped connection, a CORS failure.
   * Distinct from VALIDATION because the UI must not attribute it to a field — a form handler that
   * pins the message to "experts" turns a backend outage into "assign at least one host".
   */
  | "TRANSPORT";

/** Structured 400 codes for the OI-25024 edit endpoints. */
export type UpdateSpaceErrorCode =
  | "SESSION_CANCELLED"
  | "SESSION_COMPLETED"
  | "CAPACITY_BELOW_REGISTERED";

export interface CreateSpaceEventError {
  code: CreateSpaceEventErrorCode | UpdateSpaceErrorCode;
  message: string;
  /** Index of the session the error refers to, when applicable. */
  sessionIndex?: number;
  /** Offending host email for UNLICENSED_HOST. */
  email?: string;
  /** Current registered count for CAPACITY_BELOW_REGISTERED. */
  registeredCount?: number;
}

/** Body for PATCH /v1/convene/events/{id} — always the full editable subset. */
export interface UpdateSpaceEventRequest {
  title: string;
  description: string | null;
  /** Full replace: a company left out here is removed from the event. */
  employerIds: string[];
  /**
   * Audience name for the public landing page. Required once more than one
   * company is selected.
   */
  employerDisplayName: string | null;
  /** Post-session survey URL; null clears it. Masterclasses only. */
  surveyUrl: string | null;
  /**
   * The language `title`/`description` are written in. Must be set before any
   * translation is sent; the backend rejects a translation in this language,
   * because that copy is `title`/`description` themselves.
   */
  languageCode: ContentLanguage | null;
  /** Full replace: a language left out here is removed. */
  translations: EventTranslation[];
  /** Full replace, per session, for sessions of this event. */
  sessionTranslations: (SessionTranslation & { sessionId: string })[];
}

/** Outcome of POST /v1/convene/events/{id}/cancel — completed/cancelled sessions are skipped. */
export interface EventCancellationResult {
  cancelledSessionIds: string[];
}

/**
 * Body for PATCH /v1/convene/sessions/{id} — always the full editable subset.
 * Changing date/startTime/durationMinutes/timeZone is a reschedule (notifies
 * registered attendees via email 177); changing experts updates the Zoom
 * hosts/panelists but sends no email.
 */
export interface UpdateSessionRequest {
  /** Blank/null falls back to the event title (mirrors creation). */
  name: string | null;
  capacity: number;
  sessionInstructions: string | null;
  /** Local date, "YYYY-MM-DD". */
  date: string;
  /** Local start time, "HH:mm". */
  startTime: string;
  /** Session length in minutes; the end time is derived. */
  durationMinutes: number;
  /** IANA timezone, e.g. "Europe/Amsterdam". */
  timeZone: string;
  experts: SessionExpertInput[];
  /** Optional note included in the reschedule email; null → template fallback. */
  rescheduleAdditionalInformation: string | null;
}

/**
 * Invites and the shareable link are keyed at the SESSION (`Space`) grain:
 * each session has its own invitee list and its own shareable link, and a
 * registration is to that specific session. This matches db_design.md §3B
 * ("the unit of registration is the session") / §4.8 and schema ADR §3.5
 * (`SpaceInvite(SpaceId,…)`, one active `SpaceInviteLink` per `SpaceId`).
 * Endpoints are therefore keyed by sessionId: `/spaces/sessions/{id}/…`.
 *
 * (db_design.md §8 contemplates moving the *link* to event grain in the
 * post-WordPress target state — explicitly NOT the MVP grain.)
 */

/** Invite status over time (see SpaceInviteStatus in the schema ADR §3.6). */
export type InviteStatus =
  | "sent"
  | "opened"
  | "registered"
  | "bounced"
  | "cancelled";

/** Option A — per-person email invite. */
export interface Invite {
  id: string;
  email: string;
  status: InviteStatus;
  sentAt: string;
}

/** Option B — single company-shared registration link. */
/**
 * A shareable URL and the language it opens in. The language lives in the
 * origin, not a query parameter: MyOpenUp picks its language from the domain it
 * is served on. Minted by the API — this app displays what it is handed.
 */
export interface LocalizedLink {
  /** Locale code, e.g. "nl-NL". Empty when the event declares no language. */
  languageCode: string;
  url: string;
}

export interface InviteLink {
  /**
   * One link per language the event can be read in. A single entry when the
   * event has one language, or when the environment configures no per-language
   * domains.
   */
  urls: LocalizedLink[];
  /** Optional seat cap; null = uncapped. */
  seatCap: number | null;
  registeredCount: number;
  createdAt: string;
}

/**
 * Attendance/registration summary for a single session (Space) — the model
 * behind GET /v1/convene/sessions/{id}/attendance (S1/OI-25114, backend
 * S2/OI-25115). Registration data (funnel, roster, registered/cancelled) is
 * real today; attendance-outcome fields (attended, no-show, duration, join
 * time, rate) are null until Zoom participant reports are ingested
 * (S5/OI-25118), and the UI renders those as "available after the session".
 */
export interface SessionAttendance {
  session: {
    id: string;
    title: string;
    /** Naive local wall-clock "YYYY-MM-DDTHH:mm:ss" (mirrors SpaceSession). */
    startsAt: string;
    endsAt: string;
    timeZone: string;
    status: SessionStatus;
  };
  counts: {
    invited: number;
    registered: number;
    cancelled: number;
    /** Null until the session has run (attendance not yet ingested). */
    attended: number | null;
    noShow: number | null;
    /** Mean attended duration in seconds; null until the session has run. */
    avgDurationSeconds: number | null;
  };
  /**
   * Email-invite funnel from SpaceInvite. invited = all invites, opened =
   * opened-or-beyond, registered = registered. Distinct from `counts` because
   * shareable-link registrations create a seat with no invite row.
   */
  funnel: {
    invited: number;
    opened: number;
    registered: number;
  };
  roster: AttendanceRow[];
}

/**
 * A roster row's lifecycle. Registration states are known today; "attended"/
 * "no-show" only appear once the session runs and attendance is ingested (S5).
 */
export type AttendanceRowStatus =
  | "registered"
  | "cancelled"
  | "attended"
  | "no-show";

/** One person on a session's attendance roster (a registered `SpaceUser` seat). */
export interface AttendanceRow {
  name: string;
  email: string;
  /** Employer display name; null when unknown. */
  employer: string | null;
  /** ISO wall-clock in the session's timezone. */
  registeredAt: string;
  status: AttendanceRowStatus;
  /** Attendance columns — null until the session has run (S5). */
  joinedAt: string | null;
  durationSeconds: number | null;
  attendanceRatePercent: number | null;
}
